import React from 'react';
import { Calendar, MapPin, Briefcase, Award, Linkedin } from 'lucide-react';
import { Button } from './ui/button';

export const AboutSection = () => (
  <section id="about" className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
    <div className="max-w-7xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10 sm:mb-12">About Virtual CFO Consultancy</h2>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Virtual CFO Consultancy is the client-facing advisory practice of NSRM & Associates — a firm of Chartered Accountants with offices in Mumbai and Bangalore, and an international presence in the UAE. We bring institutional-grade financial expertise to SMEs, startups and family businesses across India who need CFO-level thinking without the full-time cost.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Unlike large firms, we give you direct access to a senior Chartered Accountant — not a junior analyst. Our team has deep experience serving Indian and GCC businesses across manufacturing, technology, retail, financial services and international trade.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-emerald-50 rounded-xl p-5 text-center">
            <Calendar className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">2018</p>
            <p className="text-xs text-gray-600">Founded</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-5 text-center">
            <MapPin className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm font-bold text-gray-900">Mumbai | Bangalore | UAE</p>
            <p className="text-xs text-gray-600">Offices</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-5 text-center">
            <Briefcase className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-xs font-bold text-gray-900 leading-snug">Manufacturing, Tech, Retail, Services, BFSI</p>
            <p className="text-xs text-gray-600 mt-1">Sectors</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-5 text-center">
            <Award className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-xs font-bold text-gray-900 leading-snug">India & Intl Taxation, CFO, Audit, Compliance, Advisory</p>
            <p className="text-xs text-gray-600 mt-1">Expertise</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const teamMembers = [
  {
    name: 'CA Navratan Soni',
    designation: 'Founding Partner & CEO',
    qualifications: 'Chartered Accountant (ICAI) | B.Com',
    photo: '/images/navratan-soni.png',
    bio: 'Navratan is the Founding Partner and CEO of NSRM & Associates with over 10 years of experience in Virtual CFO services, international taxation, business valuations and IPO/SME IPO advisory. He has previously worked with Chaturvedi & Shah, one of India\'s leading CA firms, before founding NSRM in 2018. He specialises in helping businesses expand globally and preparing companies for fundraising and public markets.',
    specialisations: ['Virtual CFO', 'International Taxation', 'Valuations', 'IPO Advisory', 'Business Expansion'],
    linkedin: 'https://www.linkedin.com/in/canavratansoni/',
    showBooking: true,
  },
  {
    name: 'CA Rajat Kapoor',
    designation: 'Co-Founding Partner',
    qualifications: 'Chartered Accountant (ICAI) | LLB',
    photo: '/images/rajat-kapoor.png',
    bio: 'Rajat is the Co-Founding Partner at NSRM & Associates, leading the firm\'s UAE and international practice. A Chartered Accountant and legal professional, he specialises in UAE corporate tax, VAT, Federal Tax Authority compliance, cross-border structuring and audit & assurance. He is a QuickBooks Certified ProAdvisor and leads the firm\'s GCC client relationships.',
    specialisations: ['UAE Tax & VAT', 'Audit & Assurance', 'Cross-Border Structuring', 'GST', 'Bookkeeping'],
    linkedin: 'https://www.linkedin.com/in/carajatk/',
    showBooking: false,
  },
];

export const TeamSection = ({ onBookAppointment }) => (
  <section id="team" className="py-16 sm:py-20 px-4 sm:px-6 bg-gray-50">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">Meet Our Partners</h2>
      <p className="text-gray-600 text-center mb-10 sm:mb-12 max-w-2xl mx-auto">Direct access to senior Chartered Accountants — not junior analysts.</p>
      <div className="grid md:grid-cols-2 gap-8">
        {teamMembers.map((m, i) => (
          <div key={m.name} className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-center mb-5">
              <img src={m.photo} alt={m.name} loading="lazy" width="112" height="112" className="w-28 h-28 rounded-full mx-auto object-cover border-4 border-emerald-100 mb-4" />
              <h3 className="text-lg font-bold text-gray-900">{m.name}</h3>
              <p className="text-sm text-emerald-600 font-medium">{m.designation}</p>
              <p className="text-xs text-gray-500 italic mt-1">{m.qualifications}</p>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-4">{m.bio}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {m.specialisations.map((s, j) => (
                <span key={j} className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full border border-emerald-200">{s}</span>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              {m.showBooking && (
                <Button onClick={onBookAppointment} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs ml-auto">
                  Book a Consultation
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const CTABanner = ({ onBookAppointment }) => (
  <section className="py-14 sm:py-16 px-4 sm:px-6 bg-gradient-to-r from-emerald-600 to-teal-600">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to get your finances under control?</h2>
      <p className="text-emerald-100 mb-8 max-w-xl mx-auto">Book a free 30-minute consultation with our CA partners. No commitment, no jargon — just honest, expert financial advice.</p>
      <Button onClick={onBookAppointment} size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-lg rounded-full shadow-xl font-semibold" data-testid="cta-banner-btn">
        Schedule a Free Call
      </Button>
    </div>
  </section>
);
